import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import {
    detectBrowserLocale,
    FALLBACK_LOCALE,
    QA_LOCALE_STORAGE_KEY,
    resolveInitialLocale,
    SUPPORTED_LOCALES,
    SupportedLocale,
} from './config';
import enAuth from './locales/en/auth.json';
import enCommon from './locales/en/common.json';
import enErrors from './locales/en/errors.json';
import enForms from './locales/en/forms.json';
import enLeague from './locales/en/league.json';
import enMatch from './locales/en/match.json';
import enSeason from './locales/en/season.json';

export const defaultNamespace = 'common';

/** All registered i18next namespaces (one JSON file per namespace per locale under `locales/{lng}/`). */
export const namespaces = ['common', 'auth', 'league', 'season', 'match', 'forms', 'errors'] as const;

export type I18nNamespace = (typeof namespaces)[number];

function syncDocumentHtmlLang(language: string): void {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = language || FALLBACK_LOCALE;
}

syncDocumentHtmlLang(resolveInitialLocale());

void i18n
    .use(initReactI18next)
    .init({
        resources: {
            en: {
                common: enCommon,
                auth: enAuth,
                league: enLeague,
                season: enSeason,
                match: enMatch,
                forms: enForms,
                errors: enErrors,
            },
        },
        lng: resolveInitialLocale(),
        fallbackLng: FALLBACK_LOCALE,
        supportedLngs: [...SUPPORTED_LOCALES],
        ns: [...namespaces],
        defaultNS: defaultNamespace,
        interpolation: {
            escapeValue: true,
        },
        react: {
            useSuspense: false,
        },
    })
    .then(() => {
        syncDocumentHtmlLang(i18n.language);
    });

i18n.on('languageChanged', (lng: string) => {
    syncDocumentHtmlLang(lng);
});

/** Persist override and switch language without reload. */
export async function setQaLocaleOverride(locale: SupportedLocale): Promise<void> {
    try {
        window.localStorage.setItem(QA_LOCALE_STORAGE_KEY, locale);
    } catch {
        // private mode / blocked storage
    }
    await i18n.changeLanguage(locale);
}

/** Remove override and return to browser-detected supported locale. */
export async function clearQaLocaleOverride(): Promise<void> {
    try {
        window.localStorage.removeItem(QA_LOCALE_STORAGE_KEY);
    } catch {
        // ignore
    }
    await i18n.changeLanguage(detectBrowserLocale());
}

export { i18n };
